import { createSelector } from 'reselect'
import { State } from 'interfaces/state'
import { Insight, InsightsDataState, WithPlacement, PlacementOf, InsightId, InsightsAppState } from 'interfaces/insights'
import { getItemsByKey } from 'helpers/normalizeHelpers'
import { createOwnPropsSelector } from 'selectors/globalSelectors'
import { InsightsOwnProps } from 'components/features/insights/Insights/Insights'
import { createInsightGroupKey } from 'helpers/insightHelpers'
import { getStaleableData, isDataUpToDate } from 'helpers/storeHelpers'
import { SocialInnerInsight } from 'interfaces/socialInsights'
import { withComparisonSelector } from 'components/features/pageFilters/selectors'

export const insightsAppSelector = (state: State): InsightsAppState =>
  state.app.insights

export const insightsDataSelector = (state: State): InsightsDataState =>
  state.data.insights

export function createVisibleInsightsByPlacementSelector <TInsight extends SocialInnerInsight | Insight, TNullablePlacement extends boolean = false> (placement?: PlacementOf<TInsight>) {
  type TPlacement = PlacementOf<TInsight>
  type TOwnProps = TNullablePlacement extends true ? Partial<WithPlacement<TPlacement>> : WithPlacement<TPlacement>
  const placementSelector = placement
    ? () => placement
    : (_: State, ownProps: TOwnProps) => ownProps.placement

  return createSelector(
    insightsDataSelector,
    placementSelector,
    insightsAppSelector,
    (insights, ownPlacement, appInsight) => {
      if (!ownPlacement) {
        return []
      }

      const ids = appInsight.order.byPlacement[ownPlacement]
      const key = createInsightGroupKey(ownPlacement)
      const data = insights && getStaleableData(insights[key])

      const result = ids && ids.length > 0 && data
        ? getItemsByKey<TInsight>(data.byKey, ids as number[])
        : []

      return result
    }
  )
}

export function createInsightByIdSelector <TInsight extends SocialInnerInsight | Insight> (placement: PlacementOf<TInsight>, insightId: InsightId) {
  return createSelector(
    insightsDataSelector,
    (insights) => {
      const key = createInsightGroupKey(placement)
      const staleable = insights && insights[key]

      type InsightCollection = NonNullable<NonNullable<InsightsDataState>[PlacementOf<TInsight>]>['data']
      const data = staleable && getStaleableData(staleable) as InsightCollection

      return insightId && data
        ? data.byKey[insightId]
        : null
    }
  )
}

export const isPlacementDataPresentSelector = createSelector(
  insightsDataSelector,
  createOwnPropsSelector<InsightsOwnProps>(),
  (insights, { placement }): boolean => {
    const key = createInsightGroupKey(placement)
    const data = insights && getStaleableData(insights[key])

    return !!data || false
  }
)

export const createIsPlacementUpToDateSelector = () => createSelector(
  insightsDataSelector,
  createOwnPropsSelector<InsightsOwnProps>(),
  withComparisonSelector,
  (insights, { placement }): boolean => {
    const key = createInsightGroupKey(placement)

    return !!insights && isDataUpToDate(insights[key])
  }
)
